import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogIn, GaugeCircle, ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * v1.2 PR-B.3 — Login with optional MFA challenge step.
 *
 *   Step 1: email + password → either logs in OR returns {mfa_required:true,
 *           mfa_challenge_token}.
 *   Step 2: 6-digit authenticator code OR backup code → /auth/login/mfa-verify
 *           → real access token + session row.
 *
 * The MFA step ALSO covers the lockout case (429 with Retry-After).
 */
export default function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA challenge step state
  const [mfaChallenge, setMfaChallenge] = useState(null);  // {mfa_challenge_token, expires_in}
  const [mfaCode, setMfaCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.mfa_required) {
        setMfaChallenge(data);
        setMfaCode("");
      } else if (data) {
        nav("/app");
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await verifyMfa(mfaChallenge.mfa_challenge_token, mfaCode.trim());
      nav("/app");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setMfaChallenge(null);
    setMfaCode("");
    setErr("");
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-navy-900 flex flex-col justify-center items-center p-5" data-testid="login-page">
      <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-logo-link">
        <div className="w-9 h-9 rounded-lg bg-navy-900 dark:bg-navy-800 flex items-center justify-center">
          <GaugeCircle className="w-5 h-5 text-orange-500" />
        </div>
        <span className="font-bold text-lg tracking-tight text-navy-900 dark:text-white">EntryBook</span>
      </Link>
      <div className="w-full max-w-md bg-white dark:bg-navy-800 rounded-2xl shadow-card border border-navy-100 dark:border-navy-700 p-8" data-testid="login-card">
        {!mfaChallenge ? (
          <>
            <div className="eb-overline mb-2">Welcome back</div>
            <h1 className="text-2xl font-semibold mb-6 text-navy-900 dark:text-white">Log in</h1>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="eb-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="eb-input"
                  data-testid="login-email-input"
                  placeholder="you@company.co.uk"
                />
              </div>
              <div>
                <label className="eb-label" htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="eb-input"
                  data-testid="login-password-input"
                />
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-xs text-orange-600 hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>
              {err && (
                <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2" data-testid="login-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="login-submit-btn">
                <LogIn className="w-4 h-4" />
                {loading ? "Logging in…" : "Log in"}
              </button>
            </form>
            <div className="mt-6 text-sm text-navy-600 dark:text-navy-300 text-center">
              New here?{" "}
              <Link to="/signup" className="font-semibold text-orange-600 hover:underline" data-testid="go-to-signup">
                Start a free trial
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="eb-overline mb-2 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-orange-500" /> Two-factor authentication
            </div>
            <h1 className="text-2xl font-semibold mb-2 text-navy-900 dark:text-white">Enter your code</h1>
            <p className="text-sm text-navy-600 dark:text-navy-300 mb-6">
              {useBackup
                ? "Enter one of your saved backup codes (single-use)."
                : "Enter the 6-digit code from your authenticator app."}
            </p>
            <form onSubmit={verify} className="space-y-4">
              <div>
                <label className="eb-label" htmlFor="mfa-code">
                  {useBackup ? "Backup code" : "Authenticator code"}
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  required
                  autoComplete="one-time-code"
                  inputMode={useBackup ? "text" : "numeric"}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="eb-input font-mono text-center text-lg tracking-widest"
                  data-testid="mfa-code-input"
                  placeholder={useBackup ? "XXXX-XXXX-XXXX" : "000000"}
                  autoFocus
                />
              </div>
              {err && (
                <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2" data-testid="mfa-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading || !mfaCode} className="btn-accent w-full" data-testid="mfa-submit-btn">
                <ShieldCheck className="w-4 h-4" />
                {loading ? "Verifying…" : "Verify & continue"}
              </button>
            </form>
            <div className="mt-4 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => { setUseBackup(!useBackup); setMfaCode(""); setErr(""); }}
                className="text-orange-600 hover:underline"
                data-testid="mfa-toggle-backup"
              >
                {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
              </button>
              <button
                type="button"
                onClick={backToPassword}
                className="text-navy-500 hover:text-navy-900 dark:text-navy-300 dark:hover:text-white inline-flex items-center gap-1"
                data-testid="mfa-back-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
